import "../styles/landing.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { Context } from "../context/AuthContext";
import logo from "../assets/img/landing_img/fff-logo-horizontal.png";
import {
  ANNUAL_PRICE,
  PromoDetails,
  checkPromoCode,
  discountLabel,
  discountTerm,
  discountedPrice,
  formatPrice,
} from "../utils/pricing";

import heroPhoto from "../assets/img/landing_img/hero-brown-trout-hand.jpg";
import ctaPhoto from "../assets/img/landing_img/cta-brown-trout.jpg";
import shotMapRings from "../assets/img/landing_img/shot-map-rings.jpg";
import shotRiverSheet from "../assets/img/landing_img/shot-river-sheet.jpg";
import shotGageDetail from "../assets/img/landing_img/shot-gage-detail.jpg";
import shotConditions from "../assets/img/landing_img/shot-conditions.jpg";
import shotMapLayers from "../assets/img/landing_img/shot-map-layers.jpg";
import joeAvatar from "../assets/img/landing_img/joe-davies-avatar.jpg";
import riverPhoto from "../assets/img/landing_img/river-angler.jpg";
// Official store badges (Apple and Google marketing guidelines: use unmodified artwork)
import appStoreBadge from "../assets/img/landing_img/app-store-badge.svg";
import googlePlayBadge from "../assets/img/landing_img/google-play-badge.png";

const APP_STORE_URL = "https://apps.apple.com/us/app/flyfishfinder-fly-fishing-hub/id6446231902";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.flyfishfinderappv2";

const TOP_PICKS = [
  { n: 1, name: "Pine Creek", meta: "53° · 34.1 mi" },
  { n: 2, name: "South Branch Root River", meta: "53° · 32.2 mi" },
];

const BITE_FACTORS = [
  { name: "Pressure", value: "steady · 30.12 inHg", rating: "Tough", tone: "tough" },
  { name: "Air temp", value: "50°", rating: "Prime", tone: "prime" },
  { name: "Sky", value: "Overcast", rating: "Prime", tone: "prime" },
  { name: "Wind", value: "8 mph", rating: "Neutral", tone: "neutral" },
];

const MAP_CHIPS = ["Access", "Gages", "Parking", "Gold Medal", "Public Lands"];

const FREE_ROWS = [
  "Maps, trout streams & lakes, fly shops",
  "Catch log, saved rivers & community",
  "Fishability Top 10: fishable rivers near you, based on flows",
  "Live gages & gage data",
];

const PRO_ROWS = [
  "Top Waters Near You, scored in real time",
  "Offline maps for those backcountry fishing trips",
  "Flow alerts, saved gages & 24h/7d flow charts",
  "Hourly Bite Forecast & best window",
  "Access, public lands, easements, ramps & more",
  "Gold Medal & wild trout waters",
  "Species Mapper",
  "3D maps",
];

const Landing = () => {
  const navigate = useNavigate();
  const user = useContext(Context);
  const [searchParams] = useSearchParams();
  const avadRaw = searchParams.get("avad");
  // Handle malformed affiliate URLs where discountcode is appended with ? instead of &
  // e.g. ?avad=419657_c4c34888d?discountcode=FFF10FISH
  const embeddedDiscount = avadRaw?.includes("?discountcode=")
    ? avadRaw.split("?discountcode=")[1]
    : null;
  const discountCode = searchParams.get("discountcode") || embeddedDiscount || null;
  // Affiliates can add &partner=Name to their link for the "Recommended by" banner
  const partner = (searchParams.get("partner") || "").trim().slice(0, 60);

  const [promo, setPromo] = useState<PromoDetails | null>(null);
  const [promoValid, setPromoValid] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    if (!!user?.user) {
      navigate("/select-plan");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!discountCode) return;
    checkPromoCode(discountCode)
      .then((result) => {
        if (result.valid) {
          localStorage.setItem("fff_discount_code", discountCode);
          setPromoValid(true);
          setPromo(result.details);
        } else {
          setPromoError(result.message);
          setTimeout(() => setPromoError(""), 5000);
        }
      })
      .catch(() => {
        // API unreachable: keep the code; checkout will reject invalid codes
        localStorage.setItem("fff_discount_code", discountCode);
        setPromoValid(true);
      });
  }, [discountCode]);

  // Sticky CTA on phones once the hero CTA has scrolled away
  useEffect(() => {
    const onScroll = () => setShowSticky(window.innerWidth < 768 && window.scrollY > 420);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const goToSignup = () => {
    const params = new URLSearchParams({ mode: "signup" });
    if (discountCode) params.set("discountcode", discountCode);
    // avad is already stored in localStorage by Router.tsx — don't pass it here
    // or Login.tsx will redirect back to landing (infinite loop)
    navigate(`/login?${params.toString()}`);
  };

  const priceLine = promo ? (
    <span>
      Then <s>{formatPrice(ANNUAL_PRICE)}</s>{" "}
      <strong className="landing-price-promo">{formatPrice(discountedPrice(promo))}</strong>
      {discountTerm(promo)}
    </span>
  ) : (
    <span>Then {formatPrice(ANNUAL_PRICE)}/year (about {formatPrice(ANNUAL_PRICE / 12)}/month)</span>
  );

  const offer = (
    <div className="landing-offer">
      <button className="landing-cta" onClick={goToSignup}>
        Try Pro Free · $0 Today
      </button>
      <div className="landing-offer-text">
        <span className="landing-offer-main">Free 7-day trial · $0 today · Cancel anytime</span>
        {priceLine}
      </div>
    </div>
  );

  return (
    <div className="landing-page">
      {!!partner && (
        <div className="landing-banner landing-banner--referral">
          <span className="landing-dot" />
          <span>
            Recommended by <strong>{partner}</strong>
          </span>
        </div>
      )}
      {!!promoError && <div className="landing-banner landing-banner--error">{promoError}</div>}
      {promoValid && !promoError && (
        <div className="landing-banner landing-banner--promo">
          {promo ? `${discountCode} applied: ${discountLabel(promo)}` : `${discountCode} applied`}
        </div>
      )}

      {/* 01 Hero */}
      <section className="landing-hero">
        <img className="landing-bg-photo landing-hero-photo" src={heroPhoto} alt="" />
        <div className="landing-hero-scrim" />
        <div className="landing-inner">
          <div className="landing-topbar">
            <img className="landing-logo" src={logo} alt="FlyFishFinder" />
            <button className="landing-login" onClick={() => navigate("/login")}>
              Log in
            </button>
          </div>
          <div className="landing-split">
            <div className="landing-hero-copy">
              <h1>Know where to fish right now.</h1>
              <p className="landing-hero-sub">Live flows, water temp and weather, ranked for 220,000 rivers.</p>
              {offer}
              <div className="landing-rating">
                <span className="landing-stars">★★★★★</span>
                <span>
                  <strong>4.8</strong> App Store rating
                </span>
              </div>
            </div>
            <div className="landing-phone-col">
              <div className="landing-phone landing-phone--dark">
                <img src={shotMapRings} alt="Fishability map: glowing rings on your top 10 gages" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 02 Fishability Index */}
      <section className="landing-section landing-section--grey">
        <div className="landing-inner landing-split">
          <div className="landing-copy">
            <div className="landing-eyebrow">
              <span className="landing-dot" />
              Fishability Index
            </div>
            <h2>Your top 10 picks right now.</h2>
            <p className="landing-lead">
              Nearby rivers, ranked by how flow, water temp and the weather line up this hour.
            </p>
            <ol className="landing-numbered">
              <li>
                <span>01</span>
                <p>
                  <strong>Creeks that share a gage are grouped.</strong> One reading, every creek it covers.
                </p>
              </li>
              <li>
                <span>02</span>
                <p>
                  <strong>Estimated readings are labeled.</strong> You always know what's measured.
                </p>
              </li>
              <li>
                <span>03</span>
                <p>
                  <strong>In-state only toggle.</strong> Keep picks inside your license.
                </p>
              </li>
            </ol>
          </div>

          <div className="landing-picks-col">
            <div className="landing-picks-photo">
              <img src={riverPhoto} alt="Angler wading a river in a mountain valley" />
            </div>
            <div className="landing-picks" aria-label="Example Fishability Top 10">
              <div className="landing-picks-toggle">
                <span>In-state only · MN</span>
                <span className="landing-switch" aria-hidden="true" />
              </div>
              <div className="landing-picks-title">Your top 10 picks right now</div>
              <div className="landing-chips">
                <span className="landing-chip">
                  <b className="landing-chip-arrow">→</b>
                  <strong>30.12</strong> inHg
                </span>
                <span className="landing-chip">
                  <strong>100%</strong> clouds
                </span>
                <span className="landing-chip">
                  <strong>8</strong> mph wind
                </span>
              </div>
              <div className="landing-best">Best tomorrow 8-11am</div>
              <div>
                {TOP_PICKS.map((r) => (
                  <div className="landing-pick" key={r.n}>
                    <span className="landing-pick-rank">{r.n}</span>
                    <div className="landing-pick-text">
                      <span className="landing-pick-name">{r.name}</span>
                      <span className="landing-pick-meta">{r.meta}</span>
                    </div>
                    <div className="landing-pick-status">
                      <span className="landing-good">GOOD</span>
                      <span className="landing-estimated">Estimated</span>
                    </div>
                  </div>
                ))}
                <div className="landing-more-creeks">
                  <span className="landing-more-title">+8 more creeks</span>
                  <span>Same readings as the Root River near Pilot Mound, MN gage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 03 Know before you drive */}
      <section className="landing-section">
        <div className="landing-inner landing-stack landing-gage">
          <div className="landing-copy landing-copy--narrow landing-copy--center">
            <div className="landing-eyebrow">Live gages · Live from USGS</div>
            <h2>Know before you drive.</h2>
            <p className="landing-lead">
              Flow vs. median, gage height vs. average, water temp and a 24h/7d chart for every gage.
            </p>
          </div>
          <div className="landing-gage-row">
            <figure className="landing-shot">
              <div className="landing-phone">
                <img src={shotRiverSheet} alt="River sheet: Root River, GOOD · FISH IT, 389 cfs steady" />
              </div>
              <figcaption>
                <strong>Root River · 389 cfs → steady.</strong> 70th percentile. Holding steady, fishable.
              </figcaption>
            </figure>
            <figure className="landing-shot">
              <div className="landing-phone">
                <img src={shotGageDetail} alt="Gage detail: flow rate, median flow, gage height, water temp, 24h flow chart" />
              </div>
              <figcaption>
                <strong>Every number in context.</strong> Today's flow next to the river's normal.
              </figcaption>
            </figure>
          </div>
          <div className="landing-alert-card">
            <div className="landing-eyebrow landing-eyebrow--light">
              <span className="landing-dot" />
              Flow Alerts
            </div>
            <p className="landing-alert-title">Get a morning alert when your river is in your range.</p>
            <p className="landing-alert-sub">Set the flows you like. We watch the gage.</p>
          </div>
        </div>
      </section>

      {/* 04 Bite forecast */}
      <section className="landing-section landing-section--navy">
        <div className="landing-inner landing-split">
          <div className="landing-copy">
            <div className="landing-eyebrow landing-eyebrow--light">Bite forecast · Next 48 hours</div>
            <h2>Hourly Bite Forecast, tuned for trout.</h2>
            <p className="landing-lead">Four factors, rated every hour. The best window is marked.</p>
            <div className="landing-factors">
              {BITE_FACTORS.map((f) => (
                <div className="landing-factor" key={f.name}>
                  <span className="landing-factor-name">{f.name}</span>
                  <span className="landing-factor-value">{f.value}</span>
                  <span className={`landing-factor-rating landing-factor-rating--${f.tone}`}>
                    <span className="landing-factor-dot" />
                    {f.rating}
                  </span>
                </div>
              ))}
            </div>
            <p className="landing-plan-ahead">
              Prefer to plan ahead? <strong>Best window tomorrow 8-11am.</strong>
            </p>
            <p className="landing-footnote">Scored for your area, not per river.</p>
          </div>
          <div className="landing-phone-col">
            <div className="landing-phone landing-phone--black">
              <img src={shotConditions} alt="Conditions: hourly score for the next 48 hours with the best window marked" />
            </div>
          </div>
        </div>
      </section>

      {/* 05 Access & public lands */}
      <section className="landing-section">
        <div className="landing-inner landing-split landing-split--reverse">
          <div className="landing-phone-col">
            <div className="landing-phone">
              <img src={shotMapLayers} alt="Satellite map with access, parking and public lands layers" />
            </div>
          </div>
          <div className="landing-copy">
            <div className="landing-eyebrow">Access · All 50 states</div>
            <h2>Find water you can actually fish.</h2>
            <p className="landing-lead">
              Easements, prime access spots, parking, ramps, campsites and public lands across the entire country.
            </p>
            <div className="landing-map-chips">
              {MAP_CHIPS.map((c) => (
                <span className="landing-map-chip" key={c}>
                  {c}
                </span>
              ))}
              <span className="landing-map-chip landing-map-chip--more">+ More</span>
            </div>
            <p className="landing-footnote landing-footnote--dark">
              Plus Species Mapper, wild trout and Gold Medal waters.
            </p>
          </div>
        </div>
      </section>

      {/* 06 Free vs Pro */}
      <section className="landing-section landing-section--grey">
        <div className="landing-compare">
          <h2>Free gets you on the map. Pro gets you on fish.</h2>
          <div className="landing-table" role="table" aria-label="Free vs Pro">
            <div className="landing-table-row landing-table-head" role="row">
              <span role="columnheader" className="landing-table-label">
                What you get
              </span>
              <span role="columnheader">Free</span>
              <span role="columnheader" className="landing-table-pro">
                Pro
              </span>
            </div>
            {FREE_ROWS.map((row) => (
              <div className="landing-table-row" role="row" key={row}>
                <span role="cell">{row}</span>
                <span role="cell" className="landing-check landing-check--free" aria-label="Included">
                  ✓
                </span>
                <span role="cell" className="landing-check" aria-label="Included">
                  ✓
                </span>
              </div>
            ))}
            {PRO_ROWS.map((row) => (
              <div className="landing-table-row" role="row" key={row}>
                <span role="cell">{row}</span>
                <span role="cell" className="landing-check landing-check--none" aria-label="Not included">
                  ·
                </span>
                <span role="cell" className="landing-check" aria-label="Included">
                  ✓
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 07 Testimonial */}
      <section className="landing-section">
        <figure className="landing-testimonial">
          <span className="landing-quote-mark">“</span>
          <blockquote>
            We used FlyFishFinder maps all day to navigate a new section... CFS and knowing when I was nearing my take
            out lead to a perfect trip for my clients.
          </blockquote>
          <figcaption>
            <img className="landing-avatar" src={joeAvatar} alt="Joe Davies" />
            <span className="landing-testimonial-who">
              <strong>Joe Davies</strong>
              <span>Head Guide, Milwaukee River Fly Fishing</span>
            </span>
          </figcaption>
        </figure>
      </section>

      {/* 08 Works on your phone + 09 Trust row */}
      <section className="landing-section landing-section--grey landing-section--short">
        <div className="landing-inner landing-inner--narrow landing-stack">
          <div className="landing-apps">
            <div className="landing-apps-copy">
              <h2 className="landing-h2-small">Works on your phone.</h2>
              <p className="landing-lead">Start your trial here, then log in on the app with the same account.</p>
            </div>
            <div className="landing-store-badges">
              <a className="landing-store" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img className="landing-store-apple" src={appStoreBadge} alt="Download on the App Store" />
              </a>
              <a className="landing-store" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img className="landing-store-google" src={googlePlayBadge} alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="landing-trust">
            <div>
              <strong>7-day free trial</strong>
              <span>Every Pro feature</span>
            </div>
            <div>
              <strong>$0 today</strong>
              <span>Nothing charged to start</span>
            </div>
            <div>
              <strong>Cancel anytime</strong>
              <span>No questions asked</span>
            </div>
          </div>
        </div>
      </section>

      {/* 10 Final CTA */}
      <section className="landing-final">
        <img className="landing-bg-photo landing-final-photo" src={ctaPhoto} alt="Brown trout released at the waterline" />
        <div className="landing-final-scrim" />
        <div className="landing-inner landing-final-content">
          <h2>Fish the best window this week.</h2>
          {offer}
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-inner landing-footer-inner">
          <span>© {new Date().getFullYear()} FlyFishFinder</span>
          <div className="landing-footer-links">
            <a href="https://www.flyfishfinder.com/terms-of-use/" target="_blank" rel="noopener noreferrer">
              Terms
            </a>
            <a href="https://www.flyfishfinder.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
              Privacy
            </a>
          </div>
        </div>
      </footer>

      {showSticky && (
        <>
          <div className="landing-sticky-spacer" />
          <div className="landing-sticky">
            <button className="landing-cta" onClick={goToSignup}>
              Try Pro Free · $0 Today
            </button>
            <span>
              {promo
                ? `$0 today · then ${formatPrice(discountedPrice(promo))} year one`
                : `$0 today · then ${formatPrice(ANNUAL_PRICE)}/year · cancel anytime`}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default Landing;
